/**
 * Resumida em tópicos SEM chamada de IA — fallback da salvia-937.
 *
 * A aba "Resumida" passou a ser gerada pelo agente `resumo_topicos` e gravada
 * em `appointments.short_summary`. Este módulo cobre o que não tem essa coluna
 * preenchida: consultas gravadas antes, consulta cujo agente falhou, e o
 * intervalo entre editar a completa e gerar de novo.
 *
 * Regra que veio das nutricionistas testadoras (T54, AJ-01): tópico NUNCA é
 * sentence cortada. A versão anterior (salvia-841) encurtava com reticências e
 * "(+N na versão completa)"; isso saiu. Aqui cada sentence do completo vira um
 * tópico inteiro, só mais seco: cai o sujeito repetido ("Paciente relata que",
 * "Refere", "O profissional orientou") e a pontuação final. Nada é descartado,
 * então não se perde dado clínico — o ganho é de leitura, não de contagem.
 *
 * Sem dependência de DOM nem de alias "@/": é importado pelo teste em node
 * (tests/test_summary_topics.mjs) e por utils/consultationSummary.js.
 */

/**
 * Frases de um parágrafo. A boundary é pontuação SEGUIDA de espaço ou fim:
 * "1.5 mg" não quebra (sem espaço depois do ponto), "Dr. Fulano" quebra — o
 * custo de errar aqui é um tópico a mais, não texto perdido.
 */
export function splitSentences(paragraph) {
  const parts = [];
  const boundary = /[.!?…]+(?=\s|$)/g;
  let start = 0;
  let m;
  while ((m = boundary.exec(paragraph)) !== null) {
    const end = m.index + m[0].length;
    const sentence = paragraph.slice(start, end).trim();
    if (sentence) parts.push(sentence);
    start = end;
  }
  const rest = paragraph.slice(start).trim();
  if (rest) parts.push(rest);
  return parts;
}

/**
 * Aberturas que só repetem quem fala. Casam no INÍCIO da sentence e são removidas
 * junto com a conjunção que as segue ("que", "de que"). Lista curta e literal
 * de propósito: errar aqui come palavra clínica.
 */
const OPENERS =
  /^(?:(?:o|a)\s+)?(?:paciente|profissional|nutricionista|m[eé]dic[oa])?\s*(?:relata|refere|informa|menciona|afirma|conta|diz|descreve|apresenta|demonstra|orient(?:ou|a)|recomend(?:ou|a)|solicit(?:ou|a))\s+(?:que\s+|de\s+que\s+)?/i;

const SUBJECT_ONLY = /^(?:o|a)\s+(?:paciente|profissional)\s+/i;

/** "Paciente relata que tem dor." → "Tem dor" */
export function toTopic(sentence) {
  let t = String(sentence ?? "").trim();
  if (!t) return "";
  // Item que já veio como lista: tira o marcador, mantém o rest.
  t = t.replace(/^[-•*]\s+/, "");
  const withoutOpener = t.replace(OPENERS, "");
  // Só aceita a poda se sobrou conteúdo de verdade.
  if (withoutOpener !== t && withoutOpener.trim().length >= 3) t = withoutOpener.trim();
  else t = t.replace(SUBJECT_ONLY, "");
  // Pontuação final some; interrogação fica (muda o sentido).
  t = t.replace(/[.;,:]+$/, "").trim();
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Conteúdo de uma seção (parágrafo ou lista em lines) → lines "- tópico". */
export function contentToTopics(content) {
  const lines = String(content ?? "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);

  // Lista: cada item já é um tópico. Parágrafo: cada sentence vira um.
  const units =
    lines.length > 1 ? lines : splitSentences(lines[0] || "");

  const seen = new Set();
  const out = [];
  for (const u of units) {
    const t = toTopic(u);
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue; // mesma sentence duas vezes no completo
    seen.add(key);
    out.push(`- ${t}`);
  }
  return out.join("\n");
}
